import { useNavigate } from 'react-router-dom'
import { Badge } from './ui/badge'
import { CalendarDays, Crown, Target, Trophy, Zap } from 'lucide-react'
import DubChampBadge from './DubChampBadge'
import EntryStatsRow from './EntryStatsRow'
import { Avatar } from './ui/avatar'

/**
 * Scope-aware leaderboard table. Column set depends on scope:
 *  - "global":     Accuracy | Brackets submitted | Champions picked
 *  - "tournament": Potential | Accuracy | Champions-alive ratio
 *  - "bracket":    Potential | Accuracy | Champion status pill
 *
 * When scope is "auto" (default) we infer from the row shape — a single
 * bracket returns `champion_status` per-entry; aggregated views return the
 * ratio fields.
 */
export default function LeaderboardTable({
  leaderboard,
  loading,
  emptyMessage = 'No rankings available',
  emptyDescription,
  onUserClick,
  scope = 'auto',
  // Group tournament view: these players won the group for a finished
  // tournament and get the dub Champ crown beside their name.
  champUserIds = null,
  // DUB-153: a tournament-scope champion ratio aggregates champion picks across
  // divisions, which reads as a confusing "2/2" on a multi-division tournament.
  // TournamentView passes false when the tournament has more than one division.
  showChampionRatio = true,
  // A single-bracket view (single-division tournament, or a division-filtered
  // leaderboard) gets the same Active/Eliminated champion pill as the division
  // leaderboard instead of the "1/1" ratio. The backend returns a real
  // per-entry champion_status for these queries.
  singleBracket = false,
  // DUB-198: before any results are entered, rows sort alphabetically and a
  // rank number (or crown) is meaningless — show a muted "–" instead. Pages
  // with a scope-global signal (paginated Leaderboards) pass this explicitly;
  // everyone else falls back to "any visible row has points".
  scoringHasStarted,
}) {
  const navigate = useNavigate()
  const ranksVisible = scoringHasStarted ?? leaderboard.some((e) => (e.total_points || 0) > 0)

  const handleUserClick = (entry) => {
    if (onUserClick) {
      onUserClick(entry)
      return
    }

    navigate(`/profile/${entry.user_id}`)
  }

  // Only #1 gets an icon (Crown — matches the "Champions" icon on /home).
  // lucide's Medal glyph bakes a "1" into the medal disc, so it rendered as a
  // FIRST-place medal on the 2nd-place row (owner DUB-178). #2 and #3 now show
  // their actual rank number, tinted to keep the old silver/bronze podium
  // colors (see rankNumberClass).
  const getRankIcon = (rank) =>
    rank === 1 ? <Crown className="h-5 w-5 text-[var(--color-crown)]" /> : null

  // Podium tint for the numeric rank: silver for 2nd, bronze (warning) for 3rd,
  // muted for everyone else.
  const rankNumberClass = (rank) =>
    rank === 2 ? 'text-slate-400' : rank === 3 ? 'text-warning' : 'text-muted-foreground'

  // A single champion pick renders as the picked champ's name (colored by
  // status; ticket 8fc2e125), falling back to an Active/Eliminated/Won pill
  // when the backend didn't resolve a name. 'not_selected'/'unknown' means no
  // champion pick (always the case for pool play) — no pill.
  const buildChampion = (status, name) =>
    (status && !['not_selected', 'unknown'].includes(status))
      ? {
          label: status === 'won' ? 'Won' : status === 'eliminated' ? 'Eliminated' : 'Active',
          status,
          name: name || null,
        }
      : null

  const buildStatsProps = (entry) => {
    const activeScope = scope === 'auto'
      ? (entry.champion_status ? 'bracket' : 'global')
      : scope

    if (activeScope === 'global') {
      return {
        potentialPoints: entry.potential_points ?? 0,
        accuracy: entry.avg_accuracy,
        accuracyNullDisplay: 'zero',
        // Label reads "Brackets submitted", so prefer the per-entry count
        // (entry_count = bracket_entries row count) over the distinct-
        // tournament count that get_leaderboard_rankings also returns.
        tournamentsEntered: entry.entry_count ?? entry.tournaments_entered ?? 0,
        championshipsWon: entry.championships_won ?? 0,
      }
    }

    if (activeScope === 'bracket') {
      return {
        potentialPoints: entry.potential_points ?? 0,
        accuracy: entry.avg_accuracy,
        accuracyNullDisplay: 'zero',
        champion: buildChampion(entry.champion_status, entry.champion_team_name),
      }
    }

    // tournament scope. A single-bracket view (single-division tournament, or a
    // division-filtered leaderboard) returns a real per-entry champion_status —
    // show the same Active/Eliminated pill as the division leaderboard rather
    // than a pointless "1/1" ratio. Multi-division views fall back to the
    // aggregated alive/total ratio (DUB-153 hides it when showChampionRatio is
    // false).
    if (singleBracket) {
      return {
        potentialPoints: entry.potential_points ?? 0,
        accuracy: entry.avg_accuracy,
        accuracyNullDisplay: 'zero',
        champion: buildChampion(entry.champion_status, entry.champion_team_name),
      }
    }

    return {
      potentialPoints: entry.potential_points ?? 0,
      accuracy: entry.avg_accuracy,
      accuracyNullDisplay: 'zero',
      championRatio: showChampionRatio
        ? {
            alive: entry.champions_alive ?? 0,
            total: entry.champion_pick_count ?? 0,
          }
        : null,
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading leaderboard...</p>
        </div>
      </div>
    )
  }

  if (leaderboard.length === 0) {
    return (
      <div className="text-center py-12">
        <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
        <p className="text-lg text-muted-foreground mb-1">{emptyMessage}</p>
        {emptyDescription && (
          <p className="text-sm text-muted-foreground">{emptyDescription}</p>
        )}
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="space-y-3 sm:space-y-2">
        {leaderboard.map((entry) => {
          const statsProps = buildStatsProps(entry)

          return (
          <div
            key={entry.user_id}
            onClick={() => handleUserClick(entry)}
            className={`p-3 sm:p-4 rounded-lg cursor-pointer transition-all duration-200 ease-out ${
              entry.is_current_user
                ? 'leaderboard-you'
                : 'bg-background border hover:bg-muted/60 hover:-translate-y-1 hover:shadow-lg'
            }`}
          >
            {/* Mobile layout (2 rows) */}
            <div className="sm:hidden">
              {/* Row 1: Rank + Name + Points */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className="w-6 flex items-center justify-center flex-shrink-0">
                    {!ranksVisible ? (
                      <span className="text-lg font-bold text-muted-foreground">–</span>
                    ) : getRankIcon(Number(entry.rank)) || (
                      <span className={`text-lg font-bold ${rankNumberClass(Number(entry.rank))}`}>
                        {entry.rank}
                      </span>
                    )}
                  </div>
                  <Avatar size="sm" fullName={entry.full_name} avatarUrl={entry.avatar_url} avatarType={entry.avatar_type} />
                  {/* Badges sit under the name on phones: inline they can't
                      shrink, and in a nested group card they squeezed the
                      name to nothing and ran over the points (2026-10-21). */}
                  <div className="flex flex-col items-start gap-1 min-w-0">
                    <span className="font-semibold min-w-0 max-w-full line-clamp-2 wrap-anywhere" title={entry.full_name || 'Unknown'}>
                      {entry.full_name || 'Unknown'}
                    </span>
                    {(entry.is_current_user || champUserIds?.includes(entry.user_id)) && (
                      <div className="flex flex-wrap items-center gap-1">
                        {entry.is_current_user && <Badge variant="secondary">You</Badge>}
                        {champUserIds?.includes(entry.user_id) && <DubChampBadge />}
                      </div>
                    )}
                  </div>
                </div>
                <p className={`flex flex-col items-end flex-shrink-0 ml-2 leading-tight text-lg font-bold whitespace-nowrap tabular-nums ${
                  entry.total_points > 0 ? 'text-primary' : 'text-muted-foreground'
                }`}>
                  {entry.total_points.toLocaleString()} <span className="text-xs font-normal">Points</span>
                </p>
              </div>

              {/* Row 2: Metrics (right-aligned) */}
              <div className="flex justify-end">
                <EntryStatsRow {...statsProps} />
              </div>
            </div>

            {/* Desktop layout (horizontal) */}
            <div className="hidden sm:flex sm:items-center sm:justify-between sm:gap-4">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="w-10 flex items-center justify-center flex-shrink-0">
                  {!ranksVisible ? (
                    <span className="text-2xl font-bold text-muted-foreground">–</span>
                  ) : getRankIcon(Number(entry.rank)) || (
                    <span className={`text-2xl font-bold ${rankNumberClass(Number(entry.rank))}`}>
                      {entry.rank}
                    </span>
                  )}
                </div>
                <Avatar size="md" fullName={entry.full_name} avatarUrl={entry.avatar_url} avatarType={entry.avatar_type} />
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-semibold min-w-0 line-clamp-2 wrap-anywhere" title={entry.full_name || 'Unknown'}>
                    {entry.full_name || 'Unknown'}
                  </span>
                  {entry.is_current_user && (
                    <Badge variant="secondary" className="shrink-0">You</Badge>
                  )}
                  {champUserIds?.includes(entry.user_id) && <DubChampBadge className="shrink-0" />}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <p className={`text-2xl font-bold whitespace-nowrap tabular-nums ${
                  entry.total_points > 0 ? 'text-primary' : 'text-muted-foreground'
                }`}>
                  {entry.total_points.toLocaleString()} <span className="text-sm font-normal">Points</span>
                </p>
                <EntryStatsRow {...statsProps} size="sm" />
              </div>
            </div>
          </div>
          )
        })}
      </div>
    </div>
  )
}
